// React/finance-app/src/AdvicePasswordsDashboard.js
// Staff page showing all advice passwords in a user-dashboard-style layout

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import api, {
  fetchUniqueSites,
  fetchMessagesIndexedByPort,
  getSessionStampWho,
  getSessionStampedAs,
  requestSimConversion,
  fetchTmAccountRequests,
} from "./api";
import {
  PortActivationBadge,
  getActivationButtonState,
  adviceRowMatchesConversionFilter,
  matchesConversionStatusFilter,
  indexPortsByAssignedDigits,
} from "./shared/portActivation";
import { tmRequestRowKey, pendingTmRequestKeySet, pendingTmRequestsByKey, completedTmByOriginalKey, completedTmByNewKey, tmAccountRoleSearchKind, adviceRowMatchesTmRoleSearch, expandAdviceRowsWithTmPairs } from "./shared/tmAccountRequest";
import InlineNotice from "./components/InlineNotice";
import AdvicePasswordsTable from "./features/user-dashboard/components/AdvicePasswordsTable";
import UnmatchedPortsTable from "./features/user-dashboard/components/UnmatchedPortsTable";
import DetailsModal from "./features/user-dashboard/components/DetailsModal";
import ReportProblemModal from "./features/user-dashboard/components/ReportProblemModal";
import ConversionStatusFilter from "./features/user-dashboard/components/ConversionStatusFilter";
import usePortsByEmail, { inferMissingPortEmails } from "./features/user-dashboard/hooks/usePortsByEmail";
import usePortActivation from "./features/user-dashboard/hooks/usePortActivation";
import { useDebounce } from "./hooks/useDebounce";
import { liveSmsUpdate, mergePortMessageMaps } from "./shared/smsMessages";

const ADVICE_INITIAL_SIZE = 500;
const ADVICE_PAGE_SIZE = 500;

export default function AdvicePasswordsDashboard() {
  useEffect(() => {
    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");
    if (!token) {
      window.location.href = "/userlogin";
      return;
    }
    if (role && role !== "staff") {
      window.location.href = role === "user" ? "/user" : "/protected";
    }
  }, []);

  const role = localStorage.getItem("role");
  const isFFStaff = role === "staff" && localStorage.getItem("staff_role") === "FF Staff";

  // Data state
  const [advice, setAdvice] = useState([]);
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [err, setErr] = useState("");

  // Activity state
  const [activityLoading, setActivityLoading] = useState(false);
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityBlocked, setActivityBlocked] = useState(false);

  // Global notice
  const [notice, setNotice] = useState(null);

  // Sorting + search
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 250);
  const [columnFilters, setColumnFilters] = useState({
    userCode: "",
    site: "",
    email: "",
    password: "",
    numbers: "",
    personaCheck: "",
    selfTransfer: "",
    conversionStatus: "",
  });
  const debouncedUserCode = useDebounce(columnFilters.userCode, 300);
  const debouncedSite = useDebounce(columnFilters.site, 300);
  const debouncedEmail = useDebounce(columnFilters.email, 300);
  const adviceListParams = useMemo(() => {
    const params = {};
    const q = (debouncedQuery || "").trim();
    if (q && !tmAccountRoleSearchKind(q)) params.search = q;
    const userCode = (debouncedUserCode || "").trim();
    const site = (debouncedSite || "").trim();
    const email = (debouncedEmail || "").trim();
    if (userCode) params.user_code_contains = userCode;
    if (site) params.site = site;
    if (email && !tmAccountRoleSearchKind(email)) params.email = email;
    if (columnFilters.personaCheck === "yes" || columnFilters.personaCheck === "no") {
      params.persona_check = columnFilters.personaCheck;
    }
    if (columnFilters.selfTransfer === "yes" || columnFilters.selfTransfer === "no") {
      params.self_transfer = columnFilters.selfTransfer;
    }
    return params;
  }, [
    debouncedQuery,
    debouncedUserCode,
    debouncedSite,
    debouncedEmail,
    columnFilters.personaCheck,
    columnFilters.selfTransfer,
  ]);
  const adviceRequestId = useRef(0);
  const loadedCountRef = useRef(0);
  const totalCountRef = useRef(0);

  const [pendingTmKeys, setPendingTmKeys] = useState(() => new Set());
  const [tmRequestsByKey, setTmRequestsByKey] = useState(() => new Map());
  const [completedTmOriginal, setCompletedTmOriginal] = useState(() => new Map());
  const [completedTmNew, setCompletedTmNew] = useState(() => new Map());

  // Row editing
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ site: "", email: "", password: "" });


  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState(null);

  // Report Problem modal
  const [showReportProblem, setShowReportProblem] = useState(false);
  const [reportProblemPort, setReportProblemPort] = useState(null);

  // Messages state: map of portId -> array of messages
  const [portMessages, setPortMessages] = useState({});

  // Multilogin overrides: { [entryId]: bool, [entryId + '_saving']: bool }
  const [multiloginOverrides, setMultiloginOverrides] = useState({});

  // Sites list for dropdown
  const [sites, setSites] = useState([]);

  const displayPorts = useMemo(
    () => inferMissingPortEmails(ports, advice),
    [ports, advice]
  );
  const portsByEmail = usePortsByEmail(displayPorts);
  const portsByDigits = useMemo(
    () => indexPortsByAssignedDigits(ports),
    [ports]
  );

  /** Hook for port activation */
  const {
    portStatus,
    portMessages: activationPortMessages,
    portCountdowns,
    clearPortMessage,
    onActivatePort,
    setPortStatusFromActivity,
    startCountdown,
    refreshActivation,
    activationLoading,
    activationError,
  } = usePortActivation(ports, setPorts, { scope: "staff", adaptivePoll: true });

  const showToast = useCallback(
    (message, kind = "info") => setNotice({ text: message, kind }),
    []
  );

  const fetchAdvicePage = useCallback(async ({ skip = 0, append = false, replaceLoaded = false } = {}) => {
    const requestId = append ? adviceRequestId.current : ++adviceRequestId.current;
    const limit = replaceLoaded
      ? Math.min(6000, Math.max(ADVICE_INITIAL_SIZE, loadedCountRef.current || ADVICE_INITIAL_SIZE))
      : !append && skip === 0
        ? ADVICE_INITIAL_SIZE
        : ADVICE_PAGE_SIZE;
    const params = { ...adviceListParams, skip: replaceLoaded ? 0 : skip, limit };
    const countParams = { ...adviceListParams };
    const [countRes, rows] = await Promise.all([
      append
        ? Promise.resolve(null)
        : api.get("/advice-passwords/count", { params: countParams }).then((r) => r.data).catch(() => ({ total: 0 })),
      api.get("/advice-passwords/", { params }).then((r) => r.data || []).catch(() => []),
    ]);
    if (adviceRequestId.current !== requestId) return;
    const pageRows = rows || [];
    const loaded = (replaceLoaded || !append ? 0 : skip) + pageRows.length;
    loadedCountRef.current = loaded;
    if (append) {
      setAdvice((prev) => {
        const seen = new Set(prev.map((row) => row.id));
        return prev.concat(pageRows.filter((row) => !seen.has(row.id)));
      });
    } else {
      setAdvice(pageRows);
      const total = Number(countRes?.total) || 0;
      totalCountRef.current = total;
      setTotalCount(total);
    }
    setHasMore(pageRows.length === limit && loaded < totalCountRef.current);
  }, [adviceListParams]);

  const refreshAdviceList = useCallback(async () => {
    await fetchAdvicePage({ replaceLoaded: true });
  }, [fetchAdvicePage]);

  useEffect(() => {
    if (activationError) setActivityError(activationError);
  }, [activationError]);

  const refreshTmAccountRequests = useCallback(async () => {
    try {
      const [pending, completed] = await Promise.all([
        fetchTmAccountRequests({ status: "pending" }).catch(() => []),
        fetchTmAccountRequests({ status: "completed" }).catch(() => []),
      ]);
      setPendingTmKeys(pendingTmRequestKeySet(pending));
      setTmRequestsByKey(pendingTmRequestsByKey(pending));
      setCompletedTmOriginal(completedTmByOriginalKey(completed));
      setCompletedTmNew(completedTmByNewKey(completed));
    } catch {
      setPendingTmKeys(new Set());
      setTmRequestsByKey(new Map());
      setCompletedTmOriginal(new Map());
      setCompletedTmNew(new Map());
    }
  }, []);

  useEffect(() => {
    refreshTmAccountRequests();
  }, [refreshTmAccountRequests]);

  useEffect(() => {
    setActivityLoading(activationLoading);
  }, [activationLoading]);

  const syncSubscriptionStatus = useCallback(async () => {
    setSubscriptionStatusLoading(true);
    setActivityError("");
    try {
      const res = await api.post("/advice-passwords/sync-subscription-status");
      const synced = res.data?.synced ?? 0;
      const updated = res.data?.updated ?? 0;
      showToast(`Subscription status synced: ${synced} user codes, ${updated} rows updated.`, "success");
      await refreshAdviceList();
    } catch (err) {
      const msg = err.response?.data?.detail ?? err.message ?? "Sync failed";
      setActivityError(typeof msg === "string" ? msg : JSON.stringify(msg));
      showToast("Subscription status sync failed", "error");
    } finally {
      setSubscriptionStatusLoading(false);
    }
  }, [showToast, refreshAdviceList]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pr, sitesList] = await Promise.all([
          api.get("/ports/").then((r) => r.data || []).catch(() => []),
          fetchUniqueSites().catch(() => []),
        ]);
        if (cancelled) return;
        setPorts(pr || []);
        setSites(sitesList || []);
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load data");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasLoadedAdvice = useRef(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (hasLoadedAdvice.current) setSearchLoading(true);
      else setLoading(true);
      try {
        await fetchAdvicePage({ skip: 0, append: false });
        if (!cancelled) setErr("");
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load data");
        }
      } finally {
        if (!cancelled) {
          hasLoadedAdvice.current = true;
          setLoading(false);
          setSearchLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchAdvicePage]);

  const loadingMoreLock = useRef(false);
  const loadMoreAdvice = useCallback(async () => {
    if (loadingMoreLock.current || !hasMore || searchLoading || loading) return;
    loadingMoreLock.current = true;
    setLoadingMore(true);
    try {
      await fetchAdvicePage({ skip: loadedCountRef.current, append: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load more");
    } finally {
      loadingMoreLock.current = false;
      setLoadingMore(false);
    }
  }, [fetchAdvicePage, hasMore, searchLoading, loading]);

  const portsRef = useRef(ports);
  const activityBlockedRef = useRef(activityBlocked);
  const setPortStatusFromActivityRef = useRef(setPortStatusFromActivity);
  const startCountdownRef = useRef(startCountdown);
  const refreshActivationRef = useRef(refreshActivation);
  portsRef.current = ports;
  activityBlockedRef.current = activityBlocked;
  setPortStatusFromActivityRef.current = setPortStatusFromActivity;
  startCountdownRef.current = startCountdown;
  refreshActivationRef.current = refreshActivation;

  /** Stays subscribed for the life of the page. A new SMS is written into the row as it arrives. */
  useEffect(() => {
    let statusTimer = null;
    const unsubscribe = api.subscribeTo("new_message", (messageData) => {
      const update = liveSmsUpdate(portsRef.current, messageData);
      if (!update) return;
      const { portId, message } = update;

      if (!activityBlockedRef.current && !message.is_outgoing) {
        setPortStatusFromActivityRef.current({
          [portId]: {
            status: "active",
            timestamp: Date.now(),
            remaining_seconds: 60,
            message_triggered: true,
          },
        });
        startCountdownRef.current(portId, 60);
        if (statusTimer) clearTimeout(statusTimer);
        statusTimer = setTimeout(() => refreshActivationRef.current(), 5500);
      }

      setPortMessages((prev) => mergePortMessageMaps(prev, { [portId]: [message] }));
    });

    return () => {
      if (statusTimer) clearTimeout(statusTimer);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);

  const digits = (s) => String(s || "").replace(/\D/g, "");

  const handleActivatePort = useCallback(
    async (phoneNumberOrPort) => {
      let phoneNumber;
      let portId;

      if (typeof phoneNumberOrPort === "string") {
        phoneNumber = phoneNumberOrPort;
        const match = ports.find(
          (p) => digits(p.assigned_number) === digits(phoneNumber)
        );
        if (!match || !match.port_number || !match.id) {
          showToast(`No port mapped to ${phoneNumber}`);
          return;
        }
        portId = match.id;
      } else if (
        typeof phoneNumberOrPort === "object" &&
        phoneNumberOrPort !== null
      ) {
        phoneNumber = phoneNumberOrPort.assigned_number;
        portId = phoneNumberOrPort.id;

        if (!phoneNumber || !portId) {
          showToast(`Invalid port data`);
          return;
        }
      } else {
        showToast(`Invalid activation parameter`);
        return;
      }

      await onActivatePort(portId, phoneNumber);
      setTimeout(() => {
        refreshActivation();
      }, 2000);
    },
    [ports, showToast, onActivatePort, refreshActivation]
  );

  const handleReportProblem = useCallback((port) => {
    setReportProblemPort(port);
    setShowReportProblem(true);
  }, []);

  const handleRequestSimConversion = useCallback(async (port) => {
    const updated = await requestSimConversion(port.id);
    setPorts((prev) => prev.map((p) => (p.id === port.id ? { ...p, ...updated } : p)));
    showToast(
      updated?.req_sim_conversion_id
        ? `TT OTP conversion requested (request #${updated.req_sim_conversion_id})`
        : "TT OTP conversion requested",
      "success"
    );
    return updated;
  }, [showToast]);

  // Handler to update port phone number (staff can update any port)
  const handleUpdatePort = useCallback(async (portId, updates) => {
    try {
      const response = await api.put(`/ports/${portId}`, updates);
      if (response.data) {
        // Update local state
        setPorts(prev => prev.map(p => p.id === portId ? response.data : p));
        setNotice({ text: "Port updated successfully", kind: "success" });
      }
    } catch (err) {
      console.error('Error updating port:', err);
      setNotice({ 
        text: err.response?.data?.detail || err.message || "Failed to update port", 
        kind: "error" 
      });
      throw err;
    }
  }, []);

  const handleToggleMultilogin = useCallback(async (entry, newValue) => {
    const id = entry.id;
    setMultiloginOverrides((prev) => ({ ...prev, [id]: newValue, [`${id}_saving`]: true }));
    try {
      await api.put(`/advice-passwords/${id}`, {
        id,
        user_code: entry.user_code,
        email: entry.email,
        site: entry.site,
        password: entry.password,
        main: entry.main,
        multilogin: newValue,
      });
      setMultiloginOverrides((prev) => {
        const next = { ...prev };
        delete next[`${id}_saving`];
        return next;
      });
    } catch (err) {
      console.error('Failed to toggle multilogin:', err);
      setMultiloginOverrides((prev) => {
        const next = { ...prev };
        delete next[id];
        delete next[`${id}_saving`];
        return next;
      });
      setNotice({ text: '✗ Failed to update multilogin', kind: 'danger' });
    }
  }, []);

  const mergeAdviceRowsMatching = useCallback((sourceRow, patch) => {
    const email = (sourceRow?.email || "").trim().toLowerCase();
    const uc = sourceRow?.user_code || "";
    setAdvice((prev) =>
      (prev || []).map((row) =>
        (row.user_code || "") === uc &&
        (row.email || "").trim().toLowerCase() === email
          ? { ...row, ...patch }
          : row
      )
    );
  }, []);

  const handlePersonaCheckSubmit = useCallback(
    async (row, { answers }) => {
      const stampedAs = getSessionStampedAs();
      const stampedBy = getSessionStampWho();
      const updated = await api.put(`/advice-passwords/${row.id}`, {
        persona_check_triggered: true,
        persona_check_stamped_as: stampedAs,
        persona_check_stamped_by: stampedBy,
        persona_check_answers: answers,
      });
      const data = updated?.data || {};
      mergeAdviceRowsMatching(row, {
        persona_check_triggered: true,
        persona_check_triggered_on:
          data.persona_check_triggered_on || new Date().toISOString(),
        persona_check_stamped_as: data.persona_check_stamped_as || stampedAs,
        persona_check_stamped_by: data.persona_check_stamped_by || stampedBy,
        persona_check_answers: answers,
      });
      setNotice({ text: "Persona check saved", kind: "success" });
    },
    [mergeAdviceRowsMatching]
  );

  const handlePersonaCheckClear = useCallback(
    async (row) => {
      await api.put(`/advice-passwords/${row.id}`, { persona_check_triggered: false });
      mergeAdviceRowsMatching(row, {
        persona_check_triggered: false,
        persona_check_triggered_on: null,
        persona_check_stamped_as: null,
        persona_check_stamped_by: null,
        persona_check_answers: null,
      });
      setNotice({ text: "Persona check cleared", kind: "success" });
    },
    [mergeAdviceRowsMatching]
  );

  const handleSelfTransferToggle = useCallback(
    async (row, checked) => {
      const updated = await api.put(`/advice-passwords/${row.id}`, {
        self_manage_transfer: checked,
      });
      const data = updated?.data || {};
      mergeAdviceRowsMatching(row, {
        self_manage_transfer: checked,
        self_manage_transfer_since: checked
          ? data.self_manage_transfer_since || new Date().toISOString()
          : null,
      });
      setNotice({
        text: checked ? "Self transfer enabled" : "Self transfer disabled",
        kind: "success",
      });
    },
    [mergeAdviceRowsMatching]
  );

  const adviceEmailSet = useMemo(() => {
    const s = new Set();
    (advice || []).forEach((r) => {
      const k = (r.email || "").trim().toLowerCase();
      if (k) s.add(k);
    });
    return s;
  }, [advice]);

  /** --------- FILTERING & GROUPING BY USER CODE --------- */
  const filteredAndGroupedAdvice = useMemo(() => {
    const qRaw = (debouncedQuery || "").trim().toLowerCase();
    const qDigits = (debouncedQuery || "").replace(/\D/g, "");
    const roleKind = tmAccountRoleSearchKind(debouncedQuery);
    const emailRoleKind = tmAccountRoleSearchKind(columnFilters.email);

    let rows = (advice || []).filter((r) => {
      const email = (r.email || "").toLowerCase();
      const site = (r.site || "").toLowerCase();
      const passwd = (r.password || "").toLowerCase();
      const userCode = (r.user_code || "").toLowerCase();

      // All linked numbers for this email, digits only, space-joined
      const linked = (portsByEmail.get(email) || [])
        .map((p) => (p?.assigned_number ? String(p.assigned_number).replace(/\D/g, "") : ""))
        .join(" ");

      // More sensitive global match (include user_code)
      const sentSearchToServer = Boolean(qRaw) && !roleKind;
      const matchesGlobal =
        sentSearchToServer ||
        !qRaw ||
        email.includes(qRaw) ||
        site.includes(qRaw) ||
        passwd.includes(qRaw) ||
        userCode.includes(qRaw) ||
        (qDigits.length >= 3 && linked.includes(qDigits)) ||
        adviceRowMatchesTmRoleSearch(r, roleKind, completedTmOriginal, completedTmNew);

      // Column filters
      const userCodeFilter = (columnFilters.userCode || "").toLowerCase();
      const siteFilter = (columnFilters.site || "").toLowerCase();
      const emailFilter = (columnFilters.email || "").toLowerCase();
      const passwordFilter = (columnFilters.password || "").toLowerCase();
      const numbersFilterDigits = (columnFilters.numbers || "").replace(/\D/g, "");
      const personaCheckFilter = columnFilters.personaCheck || "";
      const selfTransferFilter = columnFilters.selfTransfer || "";

      const matchesUserCode = !userCodeFilter || userCode.includes(userCodeFilter);
      const matchesSite = !siteFilter || site.includes(siteFilter);
      const matchesEmail =
        !emailFilter ||
        email.includes(emailFilter) ||
        adviceRowMatchesTmRoleSearch(r, emailRoleKind, completedTmOriginal, completedTmNew);
      const matchesPassword = !passwordFilter || passwd.includes(passwordFilter);
      const matchesNumbers =
        !numbersFilterDigits || linked.includes(numbersFilterDigits);
      const matchesPersonaCheck =
        !personaCheckFilter ||
        (personaCheckFilter === "yes" && !!r.persona_check_triggered) ||
        (personaCheckFilter === "no" && !r.persona_check_triggered);
      const matchesSelfTransfer =
        !selfTransferFilter ||
        (selfTransferFilter === "yes" && !!r.self_manage_transfer) ||
        (selfTransferFilter === "no" && !r.self_manage_transfer);
      const conversionStatusFilter = columnFilters.conversionStatus || "";
      const matchesConversion =
        conversionStatusFilter === "new_tt_email"
          ? pendingTmKeys.has(tmRequestRowKey(r))
          : adviceRowMatchesConversionFilter(
              r,
              portsByEmail,
              portsByDigits,
              conversionStatusFilter
            );

      return (
        matchesGlobal &&
        matchesUserCode &&
        matchesSite &&
        matchesEmail &&
        matchesPassword &&
        matchesNumbers &&
        matchesPersonaCheck &&
        matchesSelfTransfer &&
        matchesConversion
      );
    });

    // Keep order-restricted + replacement pairs together in search results.
    rows = expandAdviceRowsWithTmPairs(
      rows,
      advice,
      completedTmOriginal,
      completedTmNew
    );

    if (sortField) {
      rows = [...rows].sort((a, b) => {
        let av = a[sortField];
        let bv = b[sortField];
        if (sortField === "main") {
          av = a.main ? 1 : 0;
          bv = b.main ? 1 : 0;
        }
        av = (av ?? "").toString().toLowerCase();
        bv = (bv ?? "").toString().toLowerCase();
        const cmp = av.localeCompare(bv, undefined, { numeric: true });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    // Group by user_code
    const grouped = {};
    rows.forEach((row) => {
      const userCode = row.user_code || "UNKNOWN";
      if (!grouped[userCode]) {
        grouped[userCode] = [];
      }
      grouped[userCode].push(row);
    });

    // Convert to array format: [{ userCode, rows: [...] }, ...]
    return Object.entries(grouped)
      .map(([userCode, rows]) => ({
        userCode,
        rows: rows.sort((a, b) => {
          // Sort within group by email, then site
          const emailCmp = (a.email || "").localeCompare(b.email || "");
          if (emailCmp !== 0) return emailCmp;
          return (a.site || "").localeCompare(b.site || "");
        }),
      }))
      .sort((a, b) =>
        a.userCode.localeCompare(b.userCode, undefined, {
          numeric: true,
          sensitivity: "base",
        })
      );
  }, [advice, portsByEmail, portsByDigits, debouncedQuery, sortField, sortDir, columnFilters, pendingTmKeys, completedTmOriginal, completedTmNew]);

  // Unmatched ports
  const unmatchedPorts = useMemo(() => {
    const list = (displayPorts || []).filter((p) => {
      const k = (p.user_email || "").trim().toLowerCase();
      const num = (p.assigned_number || "").replace(/\D/g, "");
      const q = (debouncedQuery || "").toLowerCase();
      const matchesQuery =
        !q ||
        (p.user_email || "").toLowerCase().includes(q) ||
        num.includes(q.replace(/\D/g, ""));
      const conversionStatusFilter = columnFilters.conversionStatus || "";
      const matchesConversion = matchesConversionStatusFilter(
        p,
        conversionStatusFilter
      );
      return matchesQuery && matchesConversion && (!k || !adviceEmailSet.has(k));
    });
    list.sort((a, b) => {
      const av = (a.port_number || "").toString();
      const bv = (b.port_number || "").toString();
      return av.localeCompare(bv, undefined, { numeric: true });
    });
    return list;
  }, [displayPorts, adviceEmailSet, debouncedQuery, columnFilters.conversionStatus]);

  // Recent SMS only for rows on screen. Loading every port's history is what made this page slow.
  const messagePortIdsKey = useMemo(() => {
    const byDigits = new Map();
    for (const port of displayPorts || []) {
      if (!port || port.archived || port.id == null) continue;
      const num = String(port.assigned_number || "").replace(/\D/g, "");
      if (!num) continue;
      if (!byDigits.has(num)) byDigits.set(num, []);
      byDigits.get(num).push(port.id);
    }
    const ids = new Set();
    const addPort = (port) => {
      if (port?.id != null && !port.archived) ids.add(Number(port.id));
    };
    for (const group of filteredAndGroupedAdvice) {
      for (const row of group.rows || []) {
        const emailKey = (row.email || "").trim().toLowerCase();
        for (const port of portsByEmail.get(emailKey) || []) addPort(port);
        const managed = Array.isArray(row.user_managed_numbers) ? row.user_managed_numbers : [];
        for (const n of managed) {
          const digits = String(n).replace(/\D/g, "");
          for (const id of byDigits.get(digits) || []) ids.add(Number(id));
        }
      }
    }
    return Array.from(ids).sort((a, b) => a - b).join(",");
  }, [filteredAndGroupedAdvice, portsByEmail, displayPorts]);

  useEffect(() => {
    const ids = messagePortIdsKey
      ? messagePortIdsKey.split(",").map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    if (!ids.length) return undefined;
    let cancelled = false;

    (async () => {
      const messageMap = await fetchMessagesIndexedByPort(ids, 10);
      if (!cancelled) {
        setPortMessages((prev) => mergePortMessageMaps(prev, messageMap));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [messagePortIdsKey]);

  const startEdit = (r) => {
    setEditingId(r.id);
    setEditDraft({
      site: r.site || "",
      email: r.email || "",
      password: r.password || "",
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ site: "", email: "", password: "" });
  };
  const saveEdit = async (r) => {
    try {
      await api.put(`/advice-passwords/${r.id}`, {
        site: editDraft.site,
        email: editDraft.email,
        password: editDraft.password,
      });
      setNotice({ text: "Saved changes", kind: "success" });
      await refreshAdviceList();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Save failed";
      setNotice({ text: msg, kind: "danger" });
    } finally {
      cancelEdit();
    }
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };


  const getButtonState = useCallback(
    (port) => {
      const state = getActivationButtonState(port, portStatus);
      if (state.text === 'Activating…') {
        return {
          ...state,
          text: (
            <>
              <span className="spinner-border spinner-border-sm me-1" />
              Activating…
            </>
          ),
        };
      }
      return state;
    },
    [portStatus]
  );

  const getStatusBadge = useCallback(
    (port) => (
      <PortActivationBadge
        statusEntry={portStatus[port?.id] || {}}
        countdown={portCountdowns[port?.id]}
      />
    ),
    [portStatus, portCountdowns]
  );

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading advice passwords...</p>
        </div>
      </div>
    );
  }

  if (err) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger">{err}</div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <style>{`
        @keyframes badge-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        .badge.bg-warning .spinner-border { animation: badge-pulse 1.5s ease-in-out infinite; }
        .card { border-radius: 6px; }
        .card-body { padding: 0.75rem; }
        .table-sm td, .table-sm th { padding: 0.4rem; vertical-align: middle; }
        .btn { transition: all 0.2s ease; }
        .badge { font-size: 0.8rem; padding: 0.35em 0.65em; }
      `}</style>

      {/* Header */}
      <div className="row g-2 align-items-center mb-3">
        <div className="col-12 col-md-3 order-1 order-md-1">
          <h4 className="mb-0">Advice Passwords Dashboard</h4>
        </div>

        <div className="col-12 col-md-3 order-2 order-md-3 d-flex justify-content-md-end align-items-center gap-2">
          <button
            className="btn btn-sm btn-success"
            onClick={() => syncSubscriptionStatus()}
            disabled={subscriptionStatusLoading}
          >
            {subscriptionStatusLoading ? "Syncing…" : "Sync Subscription Status"}
          </button>
          <button className="btn btn-danger btn-sm" onClick={() => setShowReportProblem(true)}>
            🚨 Report Problem
          </button>
        </div>

        {/* Center: search */}
        <div className="col-12 col-md-6 order-3 order-md-2 d-flex justify-content-center align-items-center gap-2 flex-wrap">
          <input
            className="form-control form-control-sm text-center"
            placeholder="Search..."
            style={{ maxWidth: 280 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {searchLoading && (
            <span className="text-muted small">Searching…</span>
          )}
          <ConversionStatusFilter
            value={columnFilters.conversionStatus || ""}
            onChange={(conversionStatus) =>
              setColumnFilters({ ...columnFilters, conversionStatus })
            }
            showNewTtEmail
          />
        </div>
      </div>

      {/* Global notice */}
      {notice && (
        <InlineNotice
          message={notice.text}
          kind={notice.kind}
          duration={5000}
          onClose={() => setNotice(null)}
        />
      )}

      {/* Activity error */}
      {activityError && (
        <div className="alert alert-warning alert-dismissible fade show py-2" role="alert">
          <small>Status sync issue: {activityError}</small>
          <button type="button" className="btn-close" onClick={() => setActivityError("")}></button>
        </div>
      )}

      {/* Advice Passwords Table - Grouped by User Code */}
      <AdvicePasswordsTable
        filteredAdvice={filteredAndGroupedAdvice.flatMap(g => g.rows)}
        groupedByUserCode={filteredAndGroupedAdvice}
        portsByEmail={portsByEmail}
        allPorts={displayPorts}
        editingId={editingId}
        editDraft={editDraft}
        startEdit={startEdit}
        cancelEdit={cancelEdit}
        saveEdit={saveEdit}
        setEditDraft={setEditDraft}
        toggleSort={toggleSort}
        sortField={sortField}
        sortDir={sortDir}
        onActivatePort={handleActivatePort}
        onReportProblem={handleReportProblem}
        getButtonState={getButtonState}
        getStatusBadge={getStatusBadge}
        setDetailsRecord={setDetailsRecord}
        setShowDetails={setShowDetails}
        portStatus={portStatus}
        portMessages={portMessages}
        portCountdowns={portCountdowns}
        clearPortMessage={clearPortMessage}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        onUpdatePort={handleUpdatePort}
        isStaff={true}
        sites={sites}
        multiloginOverrides={multiloginOverrides}
        onToggleMultilogin={handleToggleMultilogin}
        onPersonaCheckSubmit={handlePersonaCheckSubmit}
        onPersonaCheckClear={handlePersonaCheckClear}
        onSelfTransferToggle={handleSelfTransferToggle}
        onRequestSimConversion={handleRequestSimConversion}
        simplifyMatchingProxy
        pendingTmRequestKeys={pendingTmKeys}
        tmRequestsByKey={tmRequestsByKey}
        completedTmByOriginalKey={completedTmOriginal}
        completedTmByNewKey={completedTmNew}
      />

      {(totalCount > 0 || searchLoading) && (() => {
        const visibleCount = filteredAndGroupedAdvice.reduce(
          (sum, group) => sum + (group.rows?.length || 0),
          0
        );
        const filtersNarrowedPage = visibleCount < advice.length;
        const matchTotal = filtersNarrowedPage ? visibleCount : totalCount;
        const remaining = filtersNarrowedPage ? 0 : Math.max(0, totalCount - advice.length);
        const nextBatch = Math.min(ADVICE_PAGE_SIZE, remaining);
        const loadedPct = matchTotal
          ? Math.min(100, Math.round((filtersNarrowedPage ? visibleCount : advice.length) / matchTotal * 100))
          : 0;
        return (
          <div className="text-center py-3 mt-2">
            <div className="d-flex flex-column align-items-center gap-2 px-3">
              <div className="progress w-100" style={{ maxWidth: 480, height: 8 }}>
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${loadedPct}%` }}
                  aria-valuenow={loadedPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
              <div className="text-muted small">
                {searchLoading
                  ? "Updating results…"
                  : `Showing ${visibleCount.toLocaleString()} of ${matchTotal.toLocaleString()}${
                      remaining > 0 ? ` · ${remaining.toLocaleString()} left` : " · all loaded"
                    }`}
              </div>
              {hasMore && !searchLoading && !filtersNarrowedPage && remaining > 0 && (
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={loadMoreAdvice}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Loading {nextBatch.toLocaleString()}…
                    </>
                  ) : (
                    `Load next ${nextBatch.toLocaleString()}`
                  )}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Unmatched Ports Table — hidden for FF Staff */}
      {!isFFStaff && unmatchedPorts.length > 0 && (
        <UnmatchedPortsTable
          unmatchedPorts={unmatchedPorts}
          advice={advice}
          onActivatePort={handleActivatePort}
          getButtonState={getButtonState}
          getStatusBadge={getStatusBadge}
          portStatus={portStatus}
          portMessages={activationPortMessages}
          portCountdowns={portCountdowns}
          clearPortMessage={clearPortMessage}
        />
      )}

      {/* Details modal */}
      <DetailsModal
        open={showDetails}
        onClose={() => setShowDetails(false)}
        record={detailsRecord}
        tmRequest={detailsRecord?.tmAccountRequest || null}
        isStaff
        profile={null}
        onNewEmailCreated={async () => {
          await Promise.all([refreshAdviceList(), refreshTmAccountRequests()]);
          setShowDetails(false);
          showToast("New email created", "success");
        }}
      />

      {/* Report Problem Modal */}
      <ReportProblemModal
        open={showReportProblem}
        onClose={() => {
          setShowReportProblem(false);
          setReportProblemPort(null);
        }}
        port={reportProblemPort}
      />
    </div>
  );
}

